import { NgModule } from '@angular/core';
import moment from 'moment';
import 'moment/locale/es';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { RouterModule } from '@angular/router';
import { NgxChartsModule } from '@swimlane/ngx-charts';

/**MATERIAL */
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule } from '@angular/material/list';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialogModule } from '@angular/material/dialog';
import { MatStepperModule } from '@angular/material/stepper';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSelectModule, MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatRadioModule } from '@angular/material/radio';
import { MatChipsModule } from '@angular/material/chips';
import { MatTreeModule } from '@angular/material/tree';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  MatButtonToggleModule,
  MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
} from '@angular/material/button-toggle';

/**SERVICIOS */
import { NotificationService } from './dashboard-main/notification.service';
import { AuthService } from './authentication/auth.service';
import { HttpErrorService } from './shared/http-error/http-error.service';
import { LoaderService } from './shared/loader/loader.service';
import { MessageService } from './shared/message/message.service';
import { ApplicationService } from './application/shared/application.service';
import { ApiKobraService } from './core/http/api-kobra/api-kobra.service';
import { MobileKobraService } from './core/http/mobile-kobra/mobile-kobra.service';
import { ServicesKobraService } from './core/http/services-kobra/services-kobra.service';
import { ServiciosKobraService } from './core/http/servicios-kobra/servicios-kobra.service';
import { ClientService } from './core/http/client/client.service';
import { ServiciosSaldosKobraService } from './core/http/servicios-saldos-kobra/servicios-saldos-kobra.service';
import { StorageKobraService } from './core/http/storage-kobra/storage-kobra.service';
import { NewStorageKobraService } from './core/http/new-storage-kobra/new-storage-kobra.service';
import { ServiciosBiService } from './core/http/servicios-bi/servicios-bi.service';
import { TooltipOverlayService } from './shared/components/tooltip/tooltip-overlay.service';
import { AgentService } from './agent/agent.service';
import { LoadingBarComponent } from './shared/components/loading-bar/loading-bar.component';

/**MODULOS EXTERNOS */
import { ClipboardModule } from 'ngx-clipboard';
import { NgxStarsModule } from 'ngx-stars';

@NgModule({
  declarations: [AppComponent],
  exports: [],
  bootstrap: [AppComponent],
  imports: [
    LoadingBarComponent,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatTabsModule,
    MatIconModule,
    MatSnackBarModule,
    MatFormFieldModule,
    MatListModule,
    MatGridListModule,
    MatAutocompleteModule,
    DragDropModule,
    RouterModule,
    MatSidenavModule,
    MatTooltipModule,
    MatBadgeModule,
    MatDialogModule,
    MatStepperModule,
    MatSortModule,
    MatTableModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatButtonModule,
    MatToolbarModule,
    MatSelectModule,
    MatMenuModule,
    MatPaginatorModule,
    MatCheckboxModule,
    ClipboardModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatMomentDateModule,
    MatExpansionModule,
    MatRadioModule,
    MatChipsModule,
    MatTreeModule,
    MatSlideToggleModule,
    NgxChartsModule,
    MatButtonToggleModule,
    NgxStarsModule,
  ],
  providers: [
    NotificationService,
    AuthService,
    HttpErrorService,
    LoaderService,
    MessageService,
    ApplicationService,
    ApiKobraService,
    MobileKobraService,
    ServicesKobraService,
    ServiciosKobraService,
    ClientService,
    ServiciosSaldosKobraService,
    StorageKobraService,
    NewStorageKobraService,
    ServiciosBiService,
    TooltipOverlayService,
    AgentService,
    MatDatepickerModule,
    { provide: MAT_DATE_LOCALE, useValue: 'es-MX' },
    // Material 21 dibuja una palomita (mat-pseudo-checkbox) dentro de cada
    // mat-button-toggle seleccionado. Las barras de acciones y los toggles de
    // vista del admin se disenaron sin ese indicador, asi que se apaga de
    // forma global. Con estas dos banderas el elemento ni siquiera se agrega
    // al DOM, por lo que tampoco queda el hueco que reserva a la izquierda.
    {
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      useValue: {
        hideSingleSelectionIndicator: true,
        hideMultipleSelectionIndicator: true,
      },
    },
    // Mismo criterio para el mat-select: en M3 la opcion elegida muestra una
    // palomita a la derecha que antes no existia.
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { hideSingleSelectionIndicator: true },
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor() {
    moment.locale('es');
  }
}
